/**
 * Shared CLIENT-SIDE media upload helper.
 *
 * Direct-to-S3 uploads that never store bytes (let alone base64) in the chat
 * messages DB — the catastrophic hazard that a data-URL composer attachment
 * reintroduced (a screen-recording .mp4 reached the model as an empty/`data:`
 * URL, and a success would have dumped megabytes of base64 into the turn).
 *
 * Two channels, both return the canonical S3 URL:
 *   - video/audio → presigned multipart (`/api/media/uploads`): resumable, and
 *     the `complete` step AUTO-ENQUEUES WhisperX transcription. This is the
 *     exact flow extracted from components/media/asset-library.tsx.
 *   - image/*     → pass-through form upload (`/api/media/assets`): the multipart
 *     route only accepts spoken media, so small images take the buffer path.
 *
 * Consumed by the media Asset Library (drag-drop) and the chat composer
 * (upload-on-attach).
 */

/** Mirrors ALLOWED_UPLOAD_MIME in /api/media/uploads (presigned multipart). */
export const MULTIPART_UPLOAD_MIME = new Set([
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
]);

/**
 * `accept` string for the chat composer file input + drop filter: images plus
 * the spoken-media set the multipart route allows. Anything outside this is
 * rejected at attach time (we never silently fall back to a data URL).
 */
export const CHAT_UPLOAD_ACCEPT =
  'image/*,video/mp4,video/webm,video/quicktime,audio/mpeg,audio/wav,audio/ogg';

/** Human-readable list of what the chat composer accepts (for reject toasts). */
export const CHAT_UPLOAD_SUPPORTED_LABEL =
  'images, MP4/WebM/MOV video, and MP3/WAV/OGG audio';

export type MediaUploadResult = {
  /** Canonical (bare, unsigned) S3 URL — safe to persist. */
  url: string;
  assetId?: string;
  mimeType: string;
  filename: string;
};

function normalizeMime(type: string | undefined | null): string {
  return (type || '').split(';')[0].trim().toLowerCase();
}

/** True when the chat composer should accept this file for S3 upload. */
export function isChatUploadSupportedMime(type: string | undefined | null): boolean {
  const mime = normalizeMime(type);
  return mime.startsWith('image/') || MULTIPART_UPLOAD_MIME.has(mime);
}

export interface MediaUploadOptions {
  /** Namespaces the S3 storage key + associates the asset with a session. */
  sessionId?: string;
}

/**
 * Direct-to-S3 presigned multipart upload (`/api/media/uploads`). The complete
 * step registers the asset AND auto-enqueues transcription for video/audio — do
 * not replace with the pass-through /api/media/assets form upload.
 *
 * (Extracted verbatim from asset-library.tsx; now returns the canonical URL so
 * the chat composer can attach an S3 URL instead of a data URL.)
 */
export async function uploadViaPresignedMultipart(
  file: File,
  opts: MediaUploadOptions = {},
): Promise<MediaUploadResult> {
  const createRes = await fetch('/api/media/uploads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'create',
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
      ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
    }),
  });
  const create = await createRes.json().catch(() => null);
  if (!createRes.ok) {
    throw new Error(create?.error?.toString?.() ?? `Upload init failed (${createRes.status})`);
  }
  const { uploadId, storageKey, partSize, parts } = create as {
    uploadId: string;
    storageKey: string;
    partSize: number;
    parts: Array<{ partNumber: number; url: string }>;
  };

  try {
    const etags: Array<{ partNumber: number; etag: string }> = [];
    for (const part of parts) {
      const start = (part.partNumber - 1) * partSize;
      const res = await fetch(part.url, {
        method: 'PUT',
        body: file.slice(start, Math.min(start + partSize, file.size)),
      });
      if (!res.ok) throw new Error(`Part ${part.partNumber} upload failed (${res.status})`);
      const etag = (res.headers.get('ETag') ?? '').replaceAll('"', '');
      if (!etag) {
        throw new Error('S3 did not expose the ETag header (bucket CORS needs ExposeHeaders: ETag)');
      }
      etags.push({ partNumber: part.partNumber, etag });
    }
    const completeRes = await fetch('/api/media/uploads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'complete',
        storageKey,
        uploadId,
        parts: etags,
        title: file.name,
        ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
      }),
    });
    const complete = await completeRes.json().catch(() => null);
    if (!completeRes.ok) {
      throw new Error(complete?.error?.toString?.() ?? `Upload completion failed (${completeRes.status})`);
    }
    const asset = complete?.asset as
      | { id?: string; url?: string; mimeType?: string; filename?: string | null }
      | undefined;
    if (!asset?.url) {
      throw new Error('Upload completed but no asset URL was returned.');
    }
    return {
      url: asset.url,
      assetId: asset.id,
      mimeType: asset.mimeType ?? normalizeMime(file.type),
      filename: asset.filename ?? file.name,
    };
  } catch (error) {
    // Best-effort cleanup of the dangling multipart upload.
    void fetch('/api/media/uploads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'abort', storageKey, uploadId }),
    }).catch(() => {});
    throw error;
  }
}

/**
 * Pass-through form upload for images (`/api/media/assets`) — the multipart
 * route only accepts spoken media, so small images take the buffer path. Still
 * lands in S3 and returns the canonical URL (never a data URL).
 */
async function uploadImageViaFormPost(
  file: File,
  opts: MediaUploadOptions = {},
): Promise<MediaUploadResult> {
  const formData = new FormData();
  formData.append('file', file);
  if (opts.sessionId) formData.append('sessionId', opts.sessionId);
  const res = await fetch('/api/media/assets', { method: 'POST', body: formData });
  const data = await res.json().catch(() => null);
  if (!res.ok || data?.success === false) {
    throw new Error(data?.error?.toString?.() ?? `Upload failed (${res.status})`);
  }
  const asset = Array.isArray(data?.assets) ? data.assets[0] : undefined;
  if (!asset?.fileUrl) {
    throw new Error('Upload completed but no asset URL was returned.');
  }
  return {
    url: asset.fileUrl,
    assetId: asset.id,
    mimeType: asset.mimeType ?? normalizeMime(file.type),
    filename: asset.filename ?? file.name,
  };
}

/**
 * Upload a file to the user's media library and return its canonical S3 URL.
 * Routes by MIME: spoken media → presigned multipart, images → form post.
 * Rejects anything else so the caller can surface a clear message instead of
 * silently shipping a data URL to the model.
 */
export async function uploadFileToMediaLibrary(
  file: File,
  opts: MediaUploadOptions = {},
): Promise<MediaUploadResult> {
  const mime = normalizeMime(file.type);
  if (MULTIPART_UPLOAD_MIME.has(mime)) {
    return uploadViaPresignedMultipart(file, opts);
  }
  if (mime.startsWith('image/')) {
    return uploadImageViaFormPost(file, opts);
  }
  throw new Error(
    `Unsupported file type${mime ? ` (${mime})` : ''}. Supported: ${CHAT_UPLOAD_SUPPORTED_LABEL}.`,
  );
}
